import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  TextField,
  Button,
  Container,
  Typography,
  Box,
  Snackbar,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  FormHelperText
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import apiService from '../components/ReasuableFuncations/api';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const EditProject = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [project, setProject] = useState({
    projectName: '',
    description: '',
    startDate: '',
    endDate: '',
    status: '',
    geoScope: '',
    organization: '',
    climate: ''
  });
  const [errors, setErrors] = useState({});
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  useEffect(() => {
    if (location.state?.project) {
      const { project: projectData } = location.state;
      setProject({
        ...projectData,
        startDate: projectData.startDate?.split('T')[0] || '',
        endDate: projectData.endDate?.split('T')[0] || ''
      });
    } else {
      navigate('/projects');
    }
  }, [location.state, navigate]);

  useEffect(() => {
    // Scroll to the top of the page when the component mounts
    window.scrollTo(0, 0);
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!project.projectName) newErrors.projectName = 'Project name is required';
    if (!project.description) newErrors.description = 'Description is required';
    if (!project.startDate) newErrors.startDate = 'Start date is required';
    if (!project.endDate) newErrors.endDate = 'End date is required';
    if (!project.status) newErrors.status = 'Status is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProject(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const response = await apiService.put(`/api/projects/${project._id}`, project);
      if (response.statusText === 'OK') {
        setSnackbarSeverity('success');
        setSnackbarMessage('Project updated successfully');
        setTimeout(() => navigate('/project-list'), 500);
      } else {
        setSnackbarSeverity('error');
        setSnackbarMessage(response.data.message || 'Failed to update project');
      }
    } catch (error) {
      setSnackbarSeverity('error');
      setSnackbarMessage(error.message || 'An error occurred');
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Edit Project
        </Typography>
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            margin="normal"
            name="projectName"
            label="Project Name"
            value={project.projectName || ''}
            onChange={handleInputChange}
            error={!!errors.projectName}
            helperText={errors.projectName}
          />
          <TextField
            fullWidth
            margin="normal"
            name="startDate"
            label="Start Date"
            type="date"
            value={project.startDate || ''}
            onChange={handleInputChange}
            InputLabelProps={{ shrink: true }}
            error={!!errors.startDate}
            helperText={errors.startDate}
          />
          <TextField
            fullWidth
            margin="normal"
            name="endDate"
            label="End Date"
            type="date"
            value={project.endDate || ''}
            onChange={handleInputChange}
            InputLabelProps={{ shrink: true }}
            error={!!errors.endDate}
            helperText={errors.endDate}
          />
          <FormControl 
            fullWidth 
            margin="normal"
            error={!!errors.status}
          >
            <InputLabel>Status</InputLabel>
            <Select
              name="status"
              value={project.status || ''}
              label="Status"
              onChange={handleInputChange}
            >
              <MenuItem value="Ongoing">Ongoing</MenuItem>
              <MenuItem value="Completed">Completed</MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
            </Select>
            {errors.status && <FormHelperText>{errors.status}</FormHelperText>}
          </FormControl>
          <TextField
            fullWidth
            margin="normal"
            name="description"
            label="Description"
            multiline
            rows={4}
            value={project.description || ''}
            onChange={handleInputChange}
            error={!!errors.description}
            helperText={errors.description}
          />
          <Button 
            type="submit" 
            variant="contained" 
            color="primary" 
            sx={{ mt: 2 }}
          >
            Update Project
          </Button>
        </form>
      </Box>
      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={6000}
        onClose={() => setSnackbarMessage('')}
      >
        <Alert 
          severity={snackbarSeverity}
          onClose={() => setSnackbarMessage('')}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EditProject;